import {
  findHolderMember,
  isMuttavatteenSabilType,
  normalizeWhatsappNo,
} from "./sabil-holder-audit";
import type { SabilHouseholdMemberInput } from "./sabil-household-members";

export const MIN_SABIL_AUDIT_REMARK_LENGTH = 5;

export const SABIL_AUDIT_REMARK_LABEL = "Reason for change";
export const SABIL_AUDIT_REMARK_HELPER =
  "Required for every sabil edit, including family-member changes (minimum 5 characters).";

type SabilEditFormValues = Record<string, unknown>;

function resolvePreviousHolderWhatsapp(previousData?: SabilEditFormValues): string {
  const fromColumn = normalizeWhatsappNo(previousData?.holderWhatsappNo);
  if (fromColumn) {
    return fromColumn;
  }
  const fromForm = normalizeWhatsappNo(previousData?.whatsappNo);
  if (fromForm) {
    return fromForm;
  }
  const members = previousData?.householdMembers as SabilHouseholdMemberInput[] | undefined;
  return normalizeWhatsappNo(findHolderMember(Array.isArray(members) ? members : [])?.whatsappNo);
}

/**
 * True when the edit form changes ITS or Mutawatteen holder identity fields.
 * Kept for audit labeling / API criticality helpers; the edit UI always requires a reason.
 */
export function requiresSabilEditAuditRemark(
  data: SabilEditFormValues,
  previousData?: SabilEditFormValues
): boolean {
  const sabilType = String(data.sabilType ?? previousData?.sabilType ?? "");
  const prevItsNo = String(previousData?.itsNo ?? "");
  const nextItsNo = String(data.itsNo ?? prevItsNo);

  if (nextItsNo !== prevItsNo) {
    return true;
  }

  if (!isMuttavatteenSabilType(sabilType)) {
    return false;
  }

  const prevName = String(previousData?.name ?? "");
  const nextName = String(data.name ?? prevName);
  if (nextName !== prevName) {
    return true;
  }

  const prevWhatsapp = resolvePreviousHolderWhatsapp(previousData);
  const nextWhatsapp = normalizeWhatsappNo(data.whatsappNo);
  return nextWhatsapp !== prevWhatsapp;
}

export function isValidAuditRemark(value: unknown): boolean {
  return String(value ?? "").trim().length >= MIN_SABIL_AUDIT_REMARK_LENGTH;
}

/** Every sabil edit requires a reason (including family-member-only updates). */
export function validateSabilEditAuditRemark(
  data: SabilEditFormValues,
  _previousData?: SabilEditFormValues
): string | undefined {
  if (!isValidAuditRemark(data.auditRemark)) {
    return SABIL_AUDIT_REMARK_HELPER;
  }
  return undefined;
}
